import React from 'react'
import {
  Router,
  Route,
  Redirect,
  IndexRedirect,
  IndexRoute,
  browserHistory,
  applyRouterMiddleware
} from 'react-router'
import { Provider as ReduxProvider } from 'react-redux'
import Loadable from 'react-loadable'
import { Spinner, majorScale } from '@segment/react-ui-library'
import { useScroll } from 'react-router-scroll'
import { ApolloProvider } from 'react-apollo'
import graphqlClient from './lib/graphql'
import configureStore from './store'
import scrollBehavior from './lib/scroll-behavior'
import ErrorTracker from './lib/error-tracker'
import { isOnLatest } from './lib/version-checker'
import withError from 'hoc/withError'

const withErrorHandling = withError({
  path: 'error',
  title: "Couldn't load page",
  messaging: 'An error occurred and we were unable to load this page',
  primaryButton: {
    children: 'Reload',
    onClick: () => window.location.reload()
  }
})

const Loading = withErrorHandling(() => (
  <Spinner marginY={majorScale(8)} marginX="auto" />
))

const loadable = loader => {
  return Loadable({
    loader,
    loading: Loading
  })
}

const WelcomeApp = loadable(() =>
  import(/* webpackChunkName: "Welcome" */ './containers/WelcomeApp')
)
const NotFound = loadable(() =>
  import(/* webpackChunkName: "NotFound" */ 'ui/NotFound')
)
const IndexApp = loadable(() =>
  import(/* webpackChunkName: "IndexApp" */ './containers/IndexApp')
)
const LoginApp = loadable(() =>
  import(/* webpackChunkName: "LoginApp" */ './containers/LoginApp')
)
const Logout = loadable(() =>
  import(/* webpackChunkName: "Logout" */ './containers/Logout')
)
const SignupApp = loadable(() =>
  import(/* webpackChunkName: "SignupApp" */ './containers/SignupApp')
)
const ForgotApp = loadable(() =>
  import(/* webpackChunkName: "ForgotApp" */ './containers/ForgotApp')
)
const ResetApp = loadable(() =>
  import(/* webpackChunkName: "ResetApp" */ './containers/ResetApp')
)
const ReceiptApp = loadable(() =>
  import(/* webpackChunkName: "ReceiptApp" */ './containers/ReceiptApp')
)
const WorkspaceApp = loadable(() =>
  import(/* webpackChunkName: "WorkspaceApp" */ './containers/WorkspaceApp')
)
const CustomersApp = loadable(() =>
  import(/* webpackChunkName: "CustomersApp" */ './containers/Customers/App')
)
const CustomersCreateApp = loadable(() =>
  import(/* webpackChunkName: "CustomersCreateApp" */ './containers/Customers/CreateApp')
)
const CustomersSettings = loadable(() =>
  import(/* webpackChunkName: "CustomersSettings" */ './components/Customers/Settings')
)
const CustomersSettingsSourcesApp = loadable(() =>
  import(/* webpackChunkName: "CustomersSettingsSourcesApp" */ './containers/Customers/Settings/SourcesApp')
)
const CustomersSettingsAPIAccessApp = loadable(() =>
  import(/* webpackChunkName: "CustomersSettingsAPIAccessApp" */ './containers/Customers/Settings/APIAccessApp')
)
const CustomersDestinationsApp = loadable(() =>
  import(/* webpackChunkName: "CustomersDestinationsApp" */ './containers/Customers/DestinationsApp')
)
const CustomersDestinationsViewApp = loadable(() =>
  import(/* webpackChunkName: "CustomersDestinationsViewApp" */ './containers/Customers/DestinationsApp/ViewApp')
)
const CustomersAudiencesListApp = loadable(() =>
  import(/* webpackChunkName: "CustomersAudiencesListApp" */ './containers/Customers/Audiences/ListApp')
)
const CustomersAudiencesViewApp = loadable(() =>
  import(/* webpackChunkName: "CustomersAudiencesViewApp" */ './containers/Customers/Audiences/Audience/ViewApp')
)
const CustomersAudiencesOverviewApp = loadable(() =>
  import(/* webpackChunkName: "CustomersAudiencesOverviewApp" */ './containers/Customers/Audiences/Audience/OverviewApp')
)
const CustomersAudiencesEditApp = loadable(() =>
  import(/* webpackChunkName: "CustomersAudiencesEditApp" */ './containers/Customers/Audiences/Audience/EditApp')
)
const CustomersAudiencesSettingsApp = loadable(() =>
  import(/* webpackChunkName: "CustomersAudiencesSettingsApp" */ './containers/Customers/Audiences/Audience/SettingsApp')
)
const CustomersAudiencesCreateApp = loadable(() =>
  import(/* webpackChunkName: "CustomersAudiencesCreateApp" */ './containers/Customers/Audiences/CreateApp')
)
const CustomersTraitsListApp = loadable(() =>
  import(/* webpackChunkName: "CustomersTraitsListApp" */ './containers/Customers/Traits/ListApp')
)
const CustomersTraitsCreateApp = loadable(() =>
  import(/* webpackChunkName: "CustomersTraitsCreateApp" */ './containers/Customers/Traits/CreateApp')
)
const CustomersTraitsViewApp = loadable(() =>
  import(/* webpackChunkName: "CustomersTraitsViewApp" */ './containers/Customers/Traits/ViewApp')
)
const CustomersTraitsOverviewApp = loadable(() =>
  import(/* webpackChunkName: "CustomersTraitsOverviewApp" */ './containers/Customers/Traits/OverviewApp')
)
const CustomersTraitsSettingsApp = loadable(() =>
  import(/* webpackChunkName: "CustomersTraitsSettingsApp" */ './containers/Customers/Traits/SettingsApp')
)
const CustomersTraitsEditApp = loadable(() =>
  import(/* webpackChunkName: "CustomersTraitsEditApp" */ './containers/Customers/Traits/EditApp')
)
const CustomersExplorerApp = loadable(() =>
  import(/* webpackChunkName: "CustomersExplorerApp" */ './containers/Customers/Explorer/App')
)
const CustomersExplorerProfileSheetApp = loadable(() =>
  import(/* webpackChunkName: "CustomersExplorerProfileSheetApp" */ './containers/Customers/Explorer/ProfileSheetApp')
)

const IntegrationsCatalogDetailApp = loadable(() =>
  import(/* webpackChunkName: "IntegrationsCatalogDetailApp" */ './containers/IntegrationsCatalog/DetailsApp')
)
const DestinationsCatalogApp = loadable(() =>
  import(/* webpackChunkName: "DestinationsCatalogApp" */ './containers/IntegrationsCatalogV2/DestinationsCatalogApp')
)
const SourcesCatalogApp = loadable(() =>
  import(/* webpackChunkName: "DestinationsCatalogApp" */ './containers/IntegrationsCatalogV2/SourcesCatalogApp')
)
const WorkspaceOverviewApp = loadable(() =>
  import(/* webpackChunkName: "WorkspaceOverviewApp" */ './containers/WorkspaceOverviewApp')
)
const WorkspaceQuickStart = loadable(() =>
  import(/* webpackChunkName: "WorkspaceOverviewApp" */ './containers/WorkspaceQuickStart/Quickstart')
)

const WorkspaceSettings = loadable(() =>
  import(/* webpackChunkName: "WorkspaceSettings" */ './containers/WorkspaceSettings/App')
)
const WorkspaceSettingsBasicApp = loadable(() =>
  import(/* webpackChunkName: "WorkspaceSettingsBasicApp" */ './containers/WorkspaceSettings/BasicApp')
)
const WorkspaceSettingsUsageApp = loadable(() =>
  import(/* webpackChunkName: "WorkspaceSettingsUsageApp" */ './containers/WorkspaceSettings/UsageApp')
)
const WorkspaceSettingsNotificationsApp = loadable(() =>
  import(/* webpackChunkName: "WorkspaceSettingsNotificationsApp" */ './containers/WorkspaceSettings/NotificationsApp')
)
const WorkspaceSettingsConnectionsApp = loadable(() =>
  import(/* webpackChunkName: "WorkspaceSettingsConnectionsApp" */ './containers/WorkspaceSettings/Authentication/ConnectionsApp')
)
const WorkspaceSettingsConnectionEditApp = loadable(() =>
  import(/* webpackChunkName: "WorkspaceSettingsConnectionEditApp" */ './containers/WorkspaceSettings/Authentication/ConnectionEditApp')
)
const WorkspaceSettingsConnectionSetupApp = loadable(() =>
  import(/* webpackChunkName: "WorkspaceSettingsConnectionSetupApp" */ './containers/WorkspaceSettings/Authentication/ConnectionSetupApp')
)
const WorkspaceSettingsConnectionSetupSelectApp = loadable(() =>
  import(/* webpackChunkName: "WorkspaceSettingsConnectionSetupSelectApp" */ './containers/WorkspaceSettings/Authentication/ConnectionSetupSelectApp')
)
const WorkspaceSettingsConnectionSetupConnectionApp = loadable(() =>
  import(/* webpackChunkName: "WorkspaceSettingsConnectionSetupConnectionApp" */ './containers/WorkspaceSettings/Authentication/ConnectionSetupConnectionApp')
)
const WorkspaceSettingsConnectionSetupIDPApp = loadable(() =>
  import(/* webpackChunkName: "WorkspaceSettingsConnectionSetupIDPApp" */ './containers/WorkspaceSettings/Authentication/ConnectionSetupIDPApp')
)
const WorkspaceSettingsConnectionSetupCompleteApp = loadable(() =>
  import(/* webpackChunkName: "WorkspaceSettingsConnectionSetupCompleteApp" */ './containers/WorkspaceSettings/Authentication/ConnectionSetupCompleteApp')
)
const WorkspaceSettingsDomainsApp = loadable(() =>
  import(/* webpackChunkName: "WorkspaceSettingsDomainsApp" */ './containers/WorkspaceSettings/Authentication/DomainsApp')
)
const WorkspaceSettingsAdvancedApp = loadable(() =>
  import(/* webpackChunkName: "WorkspaceSettingsAdvancedApp" */ './containers/WorkspaceSettings/Authentication/AdvancedApp')
)
const WorkspaceSettingsApiKeyApp = loadable(() =>
  import(/* webpackChunkName: "WorkspaceSettingsApiKeyApp" */ './containers/WorkspaceSettings/Authentication/ApiKeysApp/index')
)
const WorkspaceSettingsTeamApp = loadable(() =>
  import(/* webpackChunkName: "WorkspaceSettingsTeamApp" */ './containers/WorkspaceSettings/TeamApp')
)
const WorkspaceSettingsPlansApp = loadable(() =>
  import(/* webpackChunkName: "WorkspaceSettingsPlansApp" */ './containers/WorkspaceSettings/PlansApp')
)
const WorkspaceSettingsCardApp = loadable(() =>
  import(/* webpackChunkName: "WorkspaceSettingsCardApp" */ './containers/WorkspaceSettings/CardApp')
)
const WorkspaceSettingsPaymentsApp = loadable(() =>
  import(/* webpackChunkName: "WorkspaceSettingsPaymentsApp" */ './containers/WorkspaceSettings/PaymentsApp')
)
const WorkspaceSettingsPlanChangeApp = loadable(() =>
  import(/* webpackChunkName: "WorkspaceSettingsPlanChangeApp" */ './containers/WorkspaceSettings/PlanChangeApp')
)
const WorkspaceSettingsPlanChangeSelectApp = loadable(() =>
  import(/* webpackChunkName: "WorkspaceSettingsPlanChangeSelectApp" */ './containers/WorkspaceSettings/PlanChangeSelectApp')
)
const WorkspaceSettingsPlanChangeConfirmApp = loadable(() =>
  import(/* webpackChunkName: "WorkspaceSettingsPlanChangeConfirmApp" */ './containers/WorkspaceSettings/PlanChangeConfirmApp')
)
const WorkspaceSettingsPlanChangeThanksApp = loadable(() =>
  import(/* webpackChunkName: "WorkspaceSettingsPlanChangeThanksApp" */ './containers/WorkspaceSettings/PlanChangeThanksApp')
)
const WorkspaceSettingsDeletionApp = loadable(() =>
  import(/* webpackChunkName: "WorkspaceSettingsDeletionApp" */ './containers/WorkspaceSettings/WorkspaceChurn/WorkspaceDeleteApp')
)
const WorkspaceSettingsDowngradeApp = loadable(() =>
  import(/* webpackChunkName: "WorkspaceSettingsDowngradeApp" */ './containers/WorkspaceSettings/WorkspaceChurn/WorkspaceDowngradeApp')
)
const WorkspaceSettingsPrivacySecurityApp = loadable(() =>
  import(/* webpackChunkName: "WorkspaceSettingsPrivacySecurityApp" */ './containers/WorkspaceSettings/PrivacySecurityApp')
)
const WorkspaceSettingsSuppressedUsersApp = loadable(() =>
  import(/* webpackChunkName: "WorkspaceSettingsSuppressedUsersApp" */ './containers/WorkspaceSettings/SuppressedUsersApp')
)
const WorkspaceSettingsDeletionRequestsApp = loadable(() =>
  import(/* webpackChunkName: "WorkspaceSettingsDeletionRequestsApp" */ './containers/WorkspaceSettings/DeletionRequestsApp')
)

const WorkspaceDestinationsApp = loadable(() =>
  import(/* webpackChunkName: "WorkspaceDestinationsApp" */ './containers/WorkspaceDestinationsApp')
)
const WorkspacesHome = loadable(() =>
  import(/* webpackChunkName: "WorkspacesHome" */ './containers/WorkspacesHome')
)
const WorkspacesNew = loadable(() =>
  import(/* webpackChunkName: "WorkspacesNew" */ './containers/WorkspacesNew')
)

const EnableApp = loadable(() =>
  import(/* webpackChunkName: "EnableApp" */ './containers/EnableApp')
)

const SourcesApp = loadable(() =>
  import(/* webpackChunkName: "SourcesApp" */ './containers/Sources/App')
)
const LegacySourceSetup = loadable(() =>
  import(/* webpackChunkName: "LegacySourceSetup" */ './containers/Sources/SourceSetup/legacy/LegacySourceSetup')
)
const SourceApp = loadable(() =>
  import(/* webpackChunkName: "SourceApp" */ './containers/Sources/SourceApp')
)
const SourceDebugger = loadable(() =>
  import(/* webpackChunkName: "SourceDebugger" */ './containers/Sources/SourceDebuggerApp')
)
const DestinationDetails = loadable(() =>
  import(/* webpackChunkName: "DestinationDetails" */ './containers/Destinations/DestinationDetails')
)
const DestinationOverview = loadable(() =>
  import(/* webpackChunkName: "DestinationOverview" */ './containers/Destinations/DestinationOverview')
)
const DestinationConfig = loadable(() =>
  import(/* webpackChunkName: "DestinationConfig" */ './components/Destinations/DestinationConfig')
)
const DestinationDebugger = loadable(() =>
  import(/* webpackChunkName: "DestinationDebugger" */ './components/Destinations/DestinationDebugger')
)
const DestinationErrorSheet = loadable(() =>
  import(/* webpackChunkName: "DestinationErrorSheet" */ './containers/Destinations/DestinationErrors/ErrorSheet')
)
const DestinationEventDelivery = loadable(() =>
  import(/* webpackChunkName: "DestinationEventDelivery" */ './containers/Destinations/DestinationEventDelivery')
)
const SourceOverviewApp = loadable(() =>
  import(/* webpackChunkName: "SourceOverviewApp" */ './containers/Sources/SourceOverview/SourceOverviewApp')
)
const WarehouseSimulatorApp = loadable(() =>
  import(/* webpackChunkName: "WarehouseSimulatorApp" */ './containers/WarehouseSimulator/WarehouseSimulatorApp')
)
const SourceOverviewRun = loadable(() =>
  import(/* webpackChunkName: "SourceOverviewRun" */ './containers/Sources/SourceOverview/SourceOverviewRun')
)
const SourceSchemaOld = loadable(() =>
  import(/* webpackChunkName: "SourceSchemaOld" */ './containers/Sources/SourceSchema/legacy')
)
const SourceSchemaNew = loadable(() =>
  import(/* webpackChunkName: "SourceSchemaNew" */ './containers/Sources/SourceSchema/SchemaApp')
)
const ViolationsApp = loadable(() =>
  import(/* webpackChunkName: "ViolationsApp" */ './containers/Sources/ViolationsApp/index.js')
)
const ViolationSummarySheet = loadable(() =>
  import(/* webpackChunkName: "ViolationSummarySheet" */ './containers/Sources/ViolationsApp/ViolationSummarySheet.js')
)
const ProtocolsNavApp = loadable(() =>
  import(/* webpackChunkName: "ProtocolsNavApp" */ './components/Protocols/Nav')
)
const TrackingPlanEditorApp = loadable(() =>
  import(/* webpackChunkName: "TrackingPlanEditorApp" */ './containers/Sources/TrackingPlanEditorApp/index')
)
const TrackingPlanDiffApp = loadable(() =>
  import(/* webpackChunkName: "TrackingPlanDiffApp" */ './containers/Sources/TrackingPlanDiffApp')
)
const TrackingPlanListApp = loadable(() =>
  import(/* webpackChunkName: "TrackingPlanListApp" */ './containers/Sources/TrackingPlanListApp')
)
const ConnectSourceApp = loadable(() =>
  import(/* webpackChunkName: "ConnectSourceApp" */ './containers/Sources/ConnectSourceApp/index')
)
const SourceSchemaEvents = loadable(() =>
  import(/* webpackChunkName: "SourceSchemaEvents" */ './containers/Sources/SourceSchema/EventsApp')
)
const SourceSchemaIdentifyGroup = loadable(() =>
  import(/* webpackChunkName: "SourceSchemaIdentifyGroup" */ './containers/Sources/SourceSchema/IdentifyGroupApp')
)
const SourceSchemaDefaults = loadable(() =>
  import(/* webpackChunkName: "SourceSchemaDefaults" */ './containers/Sources/SourceSchema/DefaultsApp')
)
const SourceSettingsApp = loadable(() =>
  import(/* webpackChunkName: "SourceSettingsApp" */ './containers/Sources/Settings/App')
)
const SourceSettingsCrossDomainAnalyticsApp = loadable(() =>
  import(/* webpackChunkName: "SourceSettingsCrossDomainAnalyticsApp" */ './containers/Sources/Settings/CrossDomainAnalyticsApp')
)
const SourceSettingsCollaboratorsApp = loadable(() =>
  import(/* webpackChunkName: "SourceSettingsCollaboratorsApp" */ './containers/Sources/Settings/CollaboratorsApp')
)
const SourceSettingsFacebookAdsApp = loadable(() =>
  import(/* webpackChunkName: "SourceSettingsFacebookAdsApp" */ './containers/Sources/Settings/FacebookAdsApp')
)
const SourceSettingsConnectionApp = loadable(() =>
  import(/* webpackChunkName: "SourceSettingsConnectionApp" */ './containers/Sources/Settings/ConnectionApp')
)
const SourceSettingsEndpointApp = loadable(() =>
  import(/* webpackChunkName: "SourceSettingsEndpointApp" */ './components/Sources/Settings/Endpoint')
)
const SourceSettingsSchemaApp = loadable(() =>
  import(/* webpackChunkName: "SourceSettingsSchemaApp" */ './containers/Sources/Settings/SchemaApp')
)
const SourceSettingsBasicApp = loadable(() =>
  import(/* webpackChunkName: "SourceSettingsBasicApp" */ './containers/Sources/Settings/BasicApp')
)
const SourceSettingsMoveApp = loadable(() =>
  import(/* webpackChunkName: "SourceSettingsMoveApp" */ './containers/Sources/Settings/MoveApp')
)
const SourceSettingsKeysApp = loadable(() =>
  import(/* webpackChunkName: "SourceSettingsKeysApp" */ './containers/Sources/Settings/KeysApp')
)
const SourceSettingsSqlApp = loadable(() =>
  import(/* webpackChunkName: "SourceSettingsSqlApp" */ './containers/Sources/Settings/SqlApp')
)
const SourceSchemaEventProperties = loadable(() =>
  import(/* webpackChunkName: "SourceSchemaEventProperties" */ './containers/Sources/SourceSchema/EventPropertiesApp')
)

const WarehouseApp = loadable(() =>
  import(/* webpackChunkName: "WarehouseApp" */ './containers/Warehouses/Warehouse')
)
const WarehouseConnect = loadable(() =>
  import(/* webpackChunkName: "WarehouseConnect" */ './containers/Warehouses/Connect')
)
const WarehouseHistory = loadable(() =>
  import(/* webpackChunkName: "WarehouseHistory" */ './containers/Warehouses/History')
)
const WarehouseOverview = loadable(() =>
  import(/* webpackChunkName: "WarehouseOverview" */ './containers/Warehouses/Overview')
)
const WarehouseRunSheet = loadable(() =>
  import(/* webpackChunkName: "WarehouseRunSheet" */ './containers/Warehouses/RunSheet')
)
const WarehouseSettingsConnection = loadable(() =>
  import(/* webpackChunkName: "WarehouseSettingsConnection" */ './containers/Warehouses/SettingsConnection')
)
const WarehouseSettingsIndex = loadable(() =>
  import(/* webpackChunkName: "WarehouseSettingsIndex" */ './containers/Warehouses/Settings')
)
const WarehouseSettingsMaintenance = loadable(() =>
  import(/* webpackChunkName: "WarehouseSettingsMaintenance" */ './containers/Warehouses/SettingsMaintenance')
)
const WarehouseSettingsSelectiveSync = loadable(() =>
  import(/* webpackChunkName: "WarehouseSettingsSelectiveSync" */ './containers/Warehouses/SettingsSelectiveSync')
)
const WarehouseSettingsSyncSchedule = loadable(() =>
  import(/* webpackChunkName: "WarehouseSettingsSyncSchedule" */ './containers/Warehouses/SettingsSyncSchedule')
)

const WarehouseSetupApp = loadable(() =>
  import(/* webpackChunkName: "WarehouseSettingsSyncSchedule" */ './containers/Warehouses/SetupApp')
)

const PartnersApp = loadable(() =>
  import(/* webpackChunkName: "PartnersApp" */ './containers/Partners/App')
)
const PartnersCreateApp = loadable(() =>
  import(/* webpackChunkName: "PartnersCreateApp" */ './containers/Partners/CreateApp')
)
const PartnerApp = loadable(() =>
  import(/* webpackChunkName: "PartnerApp" */ './containers/Partners/Partner/App')
)
const PartnerNoteApp = loadable(() =>
  import(/* webpackChunkName: "PartnerNoteApp" */ './containers/Partners/Partner/NoteApp')
)
const PartnerJsonApp = loadable(() =>
  import(/* webpackChunkName: "PartnerJsonApp" */ './containers/Partners/Partner/JsonApp')
)
const PartnerLogosApp = loadable(() =>
  import(/* webpackChunkName: "PartnerLogosApp" */ './containers/Partners/Partner/LogosApp')
)
const PartnerReportApp = loadable(() =>
  import(/* webpackChunkName: "PartnerReportApp" */ './containers/Partners/Partner/ReportApp')
)
const PartnerProfileApp = loadable(() =>
  import(/* webpackChunkName: "PartnerProfileApp" */ './containers/Partners/Partner/ProfileApp')
)
const PartnerMethodsApp = loadable(() =>
  import(/* webpackChunkName: "PartnerMethodsApp" */ './containers/Partners/Partner/MethodsApp')
)
const PartnerPlatformsApp = loadable(() =>
  import(/* webpackChunkName: "PartnerPlatformsApp" */ './containers/Partners/Partner/PlatformsApp')
)
const PartnerCategoriesApp = loadable(() =>
  import(/* webpackChunkName: "PartnerCategoriesApp" */ './containers/Partners/Partner/CategoriesApp')
)
const PartnerReplayApp = loadable(() =>
  import(/* webpackChunkName: "PartnerReplayApp" */ './containers/Partners/Partner/ReplayApp')
)
const PartnerDirectIntegrationApp = loadable(() =>
  import(/* webpackChunkName: "PartnerDirectIntegrationApp" */ './containers/Partners/Partner/DirectIntegrationApp')
)
const PartnerComponentsApp = loadable(() =>
  import(/* webpackChunkName: "PartnerComponentsApp" */ './containers/Partners/Partner/ComponentsApp')
)
const PartnerContactsApp = loadable(() =>
  import(/* webpackChunkName: "PartnerContactsApp" */ './containers/Partners/Partner/ContactsApp')
)
const PartnerConnectionModeApp = loadable(() =>
  import(/* webpackChunkName: "PartnerConnectionModeApp" */ './containers/Partners/Partner/ConnectionModeApp')
)
const PartnerSettingsApp = loadable(() =>
  import(/* webpackChunkName: "PartnerSettingsApp" */ './containers/Partners/Partner/SettingsApp')
)
const PartnerStatusApp = loadable(() =>
  import(/* webpackChunkName: "PartnerStatusApp" */ './containers/Partners/Partner/StatusApp')
)

const store = configureStore()

function renderRoute(props) {
  /*
    Force a full page reload if app is not on the latest version.
    This will guarantee customers are always on the most recent
    version of the app the moment they decide to navigate away from
    their current path.
  */
  if (!isOnLatest()) {
    const { pathname, search } = props.location
    window.location = `${pathname}${search}`
    return null
  }

  return applyRouterMiddleware(useScroll(scrollBehavior))(props)
}

const routes = () => (
  <ReduxProvider store={store}>
    <ApolloProvider client={graphqlClient}>
      <Router
        onUpdate={function storeRoute() {
          /*
            React Router binds this function for us
            That's why we have the named old-school function definition here 😅
          */

          const route = this.router.routes.map(r => r.path).join('/')
          ErrorTracker.setRoute(route)
        }}
        history={browserHistory}
        render={renderRoute}
      >
        <Route path="/" component={IndexApp} />
        <Route path="/login" component={LoginApp} />
        <Route path="/logout" component={Logout} />
        <Route path="/signup" component={SignupApp} />
        <Route path="/forgot" component={ForgotApp} />
        <Route path="/reset" component={ResetApp} />
        <Route path="/enable" component={EnableApp} />
        <Route path="/workspaces" component={WorkspacesHome}>
          <Route path="new" component={WorkspacesNew} />
        </Route>

        <Redirect from="/partner-portal" to="/partner-portal/integration" />

        <Route path="/partner-portal/:metadataType" component={PartnersApp}>
          <Route path="create" component={PartnersCreateApp} />
          <Route path=":slug" component={PartnerApp}>
            <IndexRedirect to="profile" />
            <Route path="profile" component={PartnerProfileApp} />
            <Route path="status" component={PartnerStatusApp} />
            <Route path="metrics" component={PartnerReportApp} />
            <Route path="logos" component={PartnerLogosApp} />
            <Route path="json" component={PartnerJsonApp} />
            <Route path="note" component={PartnerNoteApp} />
            <Route path="methods" component={PartnerMethodsApp} />
            <Route path="platforms" component={PartnerPlatformsApp} />
            <Route path="categories" component={PartnerCategoriesApp} />
            <Route path="replay" component={PartnerReplayApp} />
            <Route
              path="direct-integration"
              component={PartnerDirectIntegrationApp}
            />
            <Route path="components" component={PartnerComponentsApp} />
            <Route path="contacts" component={PartnerContactsApp} />
            <Route path="settings" component={PartnerSettingsApp} />
            <Route
              path="connection-mode"
              component={PartnerConnectionModeApp}
            />
          </Route>
        </Route>

        <Route path="/:workspaceSlug" component={WorkspaceApp}>
          <IndexRedirect to="overview" />
          <Route
            path="welcome"
            component={WelcomeApp}
            disableGlobalTopNav
            disableGlobalSideNav
          />
          <Route
            path="quickstart"
            component={WorkspaceQuickStart}
            disableGlobalTopNav
            disableGlobalSideNav
          />

          <Route path="settings" component={WorkspaceSettings}>
            <IndexRedirect to="basic" />
            <Route
              path="basic"
              components={WorkspaceSettingsBasicApp}
              label="Settings"
            >
              <Route
                path="delete"
                components={WorkspaceSettingsDeletionApp}
                label="Settings"
              />
            </Route>

            <Redirect from="billing" to="usage" />
            <Redirect from="api-usage" to="usage" />
            <Route
              path="usage"
              components={WorkspaceSettingsUsageApp}
              label="Settings"
            />

            <Route
              path="notifications"
              components={WorkspaceSettingsNotificationsApp}
              label="Settings"
            />

            <Route
              path="connections"
              components={WorkspaceSettingsConnectionsApp}
              label="Settings"
            />
            <Route
              path="edit-connection/:connectionId"
              components={WorkspaceSettingsConnectionEditApp}
              label="Settings"
            />
            <Route
              path="setup-connection"
              components={WorkspaceSettingsConnectionSetupApp}
            >
              <IndexRedirect to="select" />
              <Route
                path="select"
                components={WorkspaceSettingsConnectionSetupSelectApp}
                label="Settings"
              />

              <Route
                path="configure-connection"
                components={WorkspaceSettingsConnectionSetupConnectionApp}
                label="Settings"
              />

              <Route
                path="configure-idp"
                components={WorkspaceSettingsConnectionSetupIDPApp}
                label="Settings"
              />

              <Route
                path="complete"
                components={WorkspaceSettingsConnectionSetupCompleteApp}
                label="Settings"
              />
            </Route>
            <Route
              path="domains"
              components={WorkspaceSettingsDomainsApp}
              label="Settings"
            />
            <Route
              path="advanced"
              components={WorkspaceSettingsAdvancedApp}
              label="Settings"
            />
            <Route
              path="api-keys"
              components={WorkspaceSettingsApiKeyApp}
              label="Settings"
            />
            <Route
              path="team"
              components={WorkspaceSettingsTeamApp}
              label="Settings"
            />
            <Route
              path="plans"
              components={WorkspaceSettingsPlansApp}
              label="settings"
            >
              <Route
                path="downgrade"
                components={WorkspaceSettingsDowngradeApp}
                label="Settings"
              />
            </Route>
            <Route
              path="card"
              components={WorkspaceSettingsCardApp}
              label="Settings"
            />

            <Route
              path="payments"
              components={WorkspaceSettingsPaymentsApp}
              label="Settings"
            />

            <Redirect
              path="change-plan/developer/*"
              to="change-plan/team-monthly"
            />
            <Route
              path="change-plan/:planId"
              components={WorkspaceSettingsPlanChangeApp}
              label="Settings"
            >
              <IndexRedirect to="select" />
              <Route
                path="select"
                components={WorkspaceSettingsPlanChangeSelectApp}
                ignoreScrollBehavior
                label="Settings"
              />

              <Route
                path="confirm"
                components={WorkspaceSettingsPlanChangeConfirmApp}
                label="Settings"
              />

              <Route
                path="thanks"
                components={WorkspaceSettingsPlanChangeThanksApp}
              />
            </Route>

            <Route
              path="privacy-security"
              components={WorkspaceSettingsPrivacySecurityApp}
              label="Settings"
            />

            <Route
              path="suppressed-users"
              components={WorkspaceSettingsSuppressedUsersApp}
              label="Settings"
            />

            <Route
              path="deletion-requests"
              components={WorkspaceSettingsDeletionRequestsApp}
              label="Settings"
            />
          </Route>

          <Route
            path="overview"
            component={WorkspaceOverviewApp}
            label="Overview"
          />
          <Redirect from="integrations/catalog" to="destinations/catalog" />
          <Redirect from="warehouses" to="destinations" />
          <Route
            path="destinations"
            component={WorkspaceDestinationsApp}
            label="List of Destinations"
          />
          <Route
            path="destinations/catalog"
            component={DestinationsCatalogApp}
            breadcrumb={{
              isCatalog: true
            }}
            ignoreScrollBehavior
          />
          <Route
            path="destinations/catalog/:integrationSlug"
            component={IntegrationsCatalogDetailApp}
            breadcrumb={{
              breadcrumbPath: 'destinations/catalog',
              isCatalogSetup: true
            }}
          />

          <Route
            path="destinations/warehouses/:warehouseSlug"
            component={WarehouseSetupApp}
            breadcrumb={{
              breadcrumbPath: 'destinations/catalog',
              isCatalogSetup: true
            }}
          />

          <Route path="destinations/:destinationMetadataSlug" showBreadcrumb>
            <Route
              path="overview"
              component={DestinationOverview}
              showBreadcrumb
            />
            <Route
              path="sources/:sourceSlug"
              component={DestinationDetails}
              showBreadcrumb
            >
              <Route path="configuration" component={DestinationConfig} />
              <Redirect from="simulator" to="validator" />
              <Redirect from="validator" to="event-tester" />
              <Route path="event-tester" component={DestinationDebugger} />
              <Redirect from="deliverability" to="event-delivery" />
              <Redirect from="deliverability/:type" to="event-delivery/:type" />
              <Route
                path="event-delivery"
                component={DestinationEventDelivery}
                ignoreScrollBehavior
              >
                <Route path=":type" component={DestinationErrorSheet} />
              </Route>
            </Route>
          </Route>

          <Redirect
            from="sources/:sourceSlug/overview/destinations/:integrationSlug"
            to="destinations/:integrationSlug/sources/:sourceSlug/configuration"
          />

          <Route
            path="sources"
            component={SourcesApp}
            label="List of Sources"
          />
          <Route path="compare" component={TrackingPlanDiffApp} />

          <Route path="protocols" component={ProtocolsNavApp}>
            <IndexRedirect to="tracking-plans" />
            <Route path="tracking-plans" label="Tracking Plans">
              <Route path=":trackingPlanId" component={TrackingPlanEditorApp} />
              <Route
                path="connect/:trackingPlanId"
                component={ConnectSourceApp}
              />
              <IndexRoute component={TrackingPlanListApp} />
            </Route>
          </Route>

          <Route
            path="sources/catalog(/:sourceMetadataSlug)"
            component={SourcesCatalogApp}
            breadcrumb={{
              isCatalog: true
            }}
            ignoreScrollBehavior
          />
          <Route
            path="sources/setup/:sourceMetadataSlug(/:step)"
            component={LegacySourceSetup}
            breadcrumb={{
              breadcrumbPath: 'sources/catalog',
              isCatalogSetup: true
            }}
          />
          <Route
            path="sources/:sourceSlug/schema2/events/:eventId"
            component={SourceSchemaEventProperties}
            showBreadcrumb
          />
          <Route
            path="sources/:sourceSlug"
            component={SourceApp}
            showBreadcrumb
          >
            <IndexRedirect to="overview" />
            <Redirect from="integrations" to="overview" />
            <Redirect
              from="integrations/:integrationSlug"
              to="destinations/:integrationSlug"
            />
            <Route path="overview" component={SourceOverviewApp}>
              <Route path="runs/:runId" component={SourceOverviewRun} />
            </Route>
            <Route
              path="warehouse-simulator"
              component={WarehouseSimulatorApp}
            />
            <Route path="debugger" component={SourceDebugger} />
            <Redirect from="schema" to="schema/tracks" />
            <Route
              path="schema(/:pageSlug(/:eventName))"
              component={SourceSchemaOld}
            />
            <Redirect from="schema2" to="schema2/events" />
            <Route path="schema2" component={SourceSchemaNew}>
              <IndexRedirect to="events" />
              <Route path="events" component={SourceSchemaEvents} />
              <Route path="identify" component={SourceSchemaIdentifyGroup} />
              <Route path="group" component={SourceSchemaIdentifyGroup} />
              <Route path="defaults" component={SourceSchemaDefaults} />
              <Route path="violations" component={ViolationsApp}>
                <Route path=":eventId" component={ViolationSummarySheet} />
              </Route>
            </Route>
            <Route path="settings" component={SourceSettingsApp}>
              <IndexRedirect to="basic" />
              <Route
                path="cross-domain-analytics"
                component={SourceSettingsCrossDomainAnalyticsApp}
              />
              <Route
                path="collaborators"
                component={SourceSettingsCollaboratorsApp}
              />
              <Route path="oauth" component={SourceSettingsFacebookAdsApp} />
              <Route
                path="connection"
                component={SourceSettingsConnectionApp}
              />
              <Route path="endpoint" component={SourceSettingsEndpointApp} />
              <Route path="schema" component={SourceSettingsSchemaApp} />
              <Route path="basic" component={SourceSettingsBasicApp} />
              <Route path="move" component={SourceSettingsMoveApp} />
              <Route path="keys" component={SourceSettingsKeysApp} />
              <Route path="sql" component={SourceSettingsSqlApp} />
            </Route>
          </Route>

          <Redirect path="customers" to="personas" />
          <Redirect path="customers/explorer" to="personas/explorer" />
          <Redirect path="customers/traits" to="personas/traits" />

          <Route path="personas" component={CustomersApp} disableGlobalTopNav>
            <IndexRedirect to="explorer" />
            <Route path="explorer" component={CustomersExplorerApp}>
              <Route
                path=":profileId"
                component={CustomersExplorerProfileSheetApp}
              />
            </Route>
            <Route path="new" component={CustomersCreateApp} />
            <Route path="traits" component={CustomersTraitsListApp} />
            <Route path="traits/new" component={CustomersTraitsCreateApp} />
            <Route path="traits/:traitId" component={CustomersTraitsViewApp}>
              <IndexRedirect to="overview" />
              <Route path="overview" component={CustomersTraitsOverviewApp} />
              <Route path="edit" component={CustomersTraitsEditApp} />
              <Route path="settings" component={CustomersTraitsSettingsApp} />
            </Route>
            <Route path="destinations" component={CustomersDestinationsApp}>
              <Route
                path=":destinationId"
                component={CustomersDestinationsViewApp}
              />
            </Route>
            <Route path="settings" component={CustomersSettings}>
              <IndexRedirect to="sources" />
              <Route path="sources" component={CustomersSettingsSourcesApp} />
              <Route
                path="api-access"
                component={CustomersSettingsAPIAccessApp}
              />
            </Route>

            <Route path="audiences" component={CustomersAudiencesListApp} />
            <Route
              path="audiences/new"
              component={CustomersAudiencesCreateApp}
            />
            <Route
              path="audiences/:audienceId"
              component={CustomersAudiencesViewApp}
            >
              <IndexRedirect to="overview" />
              <Route
                path="overview"
                component={CustomersAudiencesOverviewApp}
              />
              <Route path="edit" component={CustomersAudiencesEditApp} />
              <Route
                path="settings"
                component={CustomersAudiencesSettingsApp}
              />
            </Route>
          </Route>

          <Route
            path="warehouses/:warehouseId"
            component={WarehouseApp}
            showBreadcrumb
          >
            <IndexRedirect to="overview" />
            <Route path="overview" component={WarehouseOverview} />
            <Route path="connect(/:bitoolSlug)" component={WarehouseConnect} />

            <Route path="settings" component={WarehouseSettingsIndex}>
              <Route
                path="connection"
                component={WarehouseSettingsConnection}
              />
              <Route
                path="maintenance"
                component={WarehouseSettingsMaintenance}
              />
              <Route path="sync" component={WarehouseSettingsSelectiveSync} />
              <Route
                path="sync-schedule"
                component={WarehouseSettingsSyncSchedule}
              />
            </Route>

            <Route path="sync" component={WarehouseHistory} />
            <Route
              path="sync/:sourceId"
              component={WarehouseHistory}
              showBreadcrumb
            >
              <Route path=":runId" component={WarehouseRunSheet} />
            </Route>
          </Route>
        </Route>
        <Route
          path="/:workspaceSlug/settings/billing/payments/:chargeId"
          component={ReceiptApp}
        />
        <Route path="*" component={NotFound} />
      </Router>
    </ApolloProvider>
  </ReduxProvider>
)

export default routes
